import React, { useCallback, useEffect, useState } from "react";
import FullTable from "../Table/FullTable";
import useInterview from "../../utils/hooks/useInterview";

// Example 1: Student Assignment Table (with checkboxes, DnD, and multi-select drag)
export const StudentAssignmentTable = () => {
  const studentColumns = [
    {
      key: "name",
      label: "Name",
      truncate: true,
    },
    {
      key: "dateOfBirth",
      label: "Date of Birth",
      render: (item) => {
        if (!item.dateOfBirth) return "";
        const [year, month, day] = item.dateOfBirth.split("-");
        return `${day}/${month}/${year}`;
      },
    },
    {
      key: "email",
      label: "Email",
      truncate: true,
    },
  ];

  const studentData = [
    {
      id: 1,
      name: "John Doe",
      dateOfBirth: "1990-01-01",
      email: "john@example.com",
    },
    {
      id: 2,
      name: "Jane Smith",
      dateOfBirth: "1992-03-15",
      email: "jane@example.com",
    },
    {
      id: 3,
      name: "Bob Wilson",
      dateOfBirth: "1988-07-22",
      email: "bob@example.com",
    },
  ];

  const handleSelectionChange = (selectedIds) => {
    console.log("Selected students:", selectedIds);
  };

  const handleReorder = (reorderedItems) => {
    console.log("Reordered students:", reorderedItems);
  };

  const assignStudents = (selectedIds) => {
    console.log("Assigning students:", selectedIds);
    alert(`Assigned ${selectedIds.length} students`);
  };

  return (
    <FullTable
      title="Assign Students"
      data={studentData}
      columns={studentColumns}
      enableCheckbox={true}
      enableDnd={true}
      enableSearch={true}
      multiSelectDrag={true}
      searchFields={["name", "email"]}
      searchPlaceholder="Search by name or email..."
      onSelectionChange={handleSelectionChange}
      onReorder={handleReorder}
      actionButtons={[
        {
          label: "Assign",
          onClick: assignStudents,
          requiresSelection: true,
          className: "bg-blue-500 text-white hover:bg-blue-600",
        },
      ]}
    />
  );
};

// Example 2: Question Bank Table (like your existing QuestionBank.jsx)
export const QuestionBankTable = () => {
  const { fetchQuestionsBank, deleteOneQuestion } = useInterview();

  const questionColumns = [
    {
      key: "title",
      label: "Title",
      truncate: true,
      className: "max-w-xs",
    },
    {
      key: "question",
      label: "Question",
      truncate: true,
      className: "max-w-sm",
    },
    {
      key: "actions",
      label: "",
      width: "w-8",
      render: (item) => (
        <button
          onClick={() => console.log("Edit question:", item.id)}
          className="text-blue-500 hover:text-blue-700"
        >
          ✏️
        </button>
      ),
    },
  ];

  const handleDelete = async (selectedIds) => {
    const results = await Promise.all(
      selectedIds.map((id) => deleteOneQuestion(id))
    );
    console.log("Delete results:", results);
  };

  // Memoize fetchQuestions to prevent infinite re-renders
  const fetchQuestions = useCallback(async () => {
    const result = await fetchQuestionsBank("", 1, 10);
    if (result.success) {
      const mapped = result.data.results.map(({ uuid, name, body }) => ({
        id: uuid,
        title: name,
        question: body,
      }));
      return { success: true, data: mapped };
    }
    return result;
  }, [fetchQuestionsBank]);

  return (
    <FullTable
      title="Question Bank"
      columns={questionColumns}
      enableCheckbox={true}
      enableDnd={false}
      enableSearch={true}
      searchFields={["title", "question"]}
      searchPlaceholder="Search questions..."
      fetchData={fetchQuestions}
      actionButtons={[
        {
          label: "Delete Questions",
          onClick: handleDelete,
          requiresSelection: true,
          className: "bg-red-500 text-white hover:bg-red-600",
        },
        {
          label: "+ Create Question",
          onClick: () => console.log("Create new question"),
          requiresSelection: false,
          className: "bg-green-500 text-white hover:bg-green-600",
        },
      ]}
    />
  );
};

// Example 3: Simple Read-Only Table (no checkboxes, no DnD)
export const ReadOnlyTable = () => {
  const columns = [
    { key: "name", label: "Name" },
    { key: "status", label: "Status" },
    { key: "date", label: "Date" },
  ];

  const data = [
    { id: 1, name: "Task 1", status: "Completed", date: "2024-01-01" },
    { id: 2, name: "Task 2", status: "Pending", date: "2024-01-02" },
  ];

  return (
    <FullTable
      title="Task Overview"
      data={data}
      columns={columns}
      enableCheckbox={false}
      enableDnd={false}
      enableSearch={false}
      enableSelection={false}
    />
  );
};

// Example 4: Interview Questions with DnD Reordering (no checkboxes)
export const InterviewQuestionsTable = ({ interviewId }) => {
  const { fetchQuestionsInterview, updateQuestionSequence, fetchOneQuestion } =
    useInterview();

  const columns = [
    {
      key: "name",
      label: "Title",
      truncate: true,
    },
    {
      key: "body",
      label: "Question",
      truncate: true,
    },
    {
      key: "actions",
      label: "",
      render: (item) => (
        <button
          onClick={() => console.log("Remove question:", item.id)}
          className="text-red-500 hover:text-red-700"
        >
          🗑️
        </button>
      ),
    },
  ];

  const handleReorder = async (reorderedItems) => {
    // Update sequence for each question
    for (let i = 0; i < reorderedItems.length; i++) {
      await updateQuestionSequence(reorderedItems[i].uuid, i + 1);
    }
  };

  // Memoize fetchQuestions to prevent infinite re-renders
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    const fetchQuestions = async () => {
      const result = await fetchQuestionsInterview("", 1, 50, interviewId);

      if (!result.success) {
        return result;
      }

      // Fix: Use Promise.all to wait for all async operations
      const dataPromises = result.data.results.map(async (e) => {
        console.log("e:", e);
        const question = await fetchOneQuestion(e.question);
        console.log("question", question);
        return {
          uuid: e.uuid,
          name: question?.data?.name,
          body: question?.data?.body,
        };
      });

      // Wait for all promises to resolve
      const dataList = await Promise.all(dataPromises);
      console.log("resolved dataList:", dataList);

      setQuestions(dataList);
    };
    fetchQuestions();
  }, []);

  return (
    <FullTable
      data={questions}
      title="Interview Questions"
      columns={columns}
      enableCheckbox={true}
      enableDnd={true}
      multiSelectDrag={true}
      enableSearch={true}
      searchFields={["name", "body"]}
      searchPlaceholder="Search questions..."
      onReorder={handleReorder}
      emptyMessage="No questions added to this interview"
    />
  );
};

// Example 5: Multi-Select with Custom Actions
export const MultiSelectActionsTable = () => {
  const columns = [
    { key: "name", label: "User Name" },
    { key: "role", label: "Role" },
    { key: "department", label: "Department" },
  ];

  const userData = [
    {
      id: 1,
      name: "Alice Johnson",
      role: "Developer",
      department: "Engineering",
    },
    { id: 2, name: "Bob Smith", role: "Designer", department: "Design" },
    { id: 3, name: "Carol Davis", role: "Manager", department: "Product" },
  ];

  const bulkActions = [
    {
      label: "Send Email",
      onClick: (selectedIds) => console.log("Sending email to:", selectedIds),
      requiresSelection: true,
      className: "bg-blue-500 text-white hover:bg-blue-600",
    },
    {
      label: "Export CSV",
      onClick: (selectedIds, allItems) =>
        console.log("Exporting:", selectedIds.length ? selectedIds : allItems),
      requiresSelection: false,
      className: "bg-green-500 text-white hover:bg-green-600",
    },
    {
      label: "Archive",
      onClick: (selectedIds) => console.log("Archiving:", selectedIds),
      requiresSelection: true,
      className: "bg-yellow-500 text-white hover:bg-yellow-600",
    },
  ];

  return (
    <FullTable
      title="User Management"
      data={userData}
      columns={columns}
      enableCheckbox={true}
      enableDnd={false}
      enableSearch={true}
      searchFields={["name", "role", "department"]}
      searchPlaceholder="Search users..."
      actionButtons={bulkActions}
    />
  );
};

export default {
  StudentAssignmentTable,
  QuestionBankTable,
  ReadOnlyTable,
  InterviewQuestionsTable,
  MultiSelectActionsTable,
};
